/**
 * Resolve the user-facing event title.
 * Order: display_name_override → display_name → event_name.
 * Search continues to match event_name (full original title).
 */

export function resolveEventDisplayName(event) {
  if (event == null) {
    return 'Event';
  }
  const override =
    typeof event.display_name_override === 'string' ? event.display_name_override.trim() : '';
  if (override) {
    return override;
  }
  const display = typeof event.display_name === 'string' ? event.display_name.trim() : '';
  if (display) {
    return display;
  }
  const original = typeof event.event_name === 'string' ? event.event_name.trim() : '';
  return original || 'Event';
}

// ---------------------------------------------------------------------------
// The event page's second line: what the title says beyond the billed acts,
// usually the tour ("Pedro Fernández - Ave Fénix Two'r" -> "Ave Fénix Two'r").
// null when nothing is left. Names match with case, accents and punctuation
// folded, as derive_event_display_name (149) does on the server.

const fold = (s) =>
  String(s || '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');

/** A regex source for a name, run against NFD text with flags 'iu'. */
function namePattern(name) {
  const trimmed = String(name || '').trim();
  const hasThe = /^the\s/i.test(trimmed);
  const chars = [...fold(hasThe ? trimmed.replace(/^the\s+/i, '') : trimmed)];
  if (chars.length === 0) return null;
  const body = chars
    .map((c) => `${c.replace(/[$()*+.?[\\\]^{|}]/g, '\\$&')}\\p{M}*`)
    .join('[^\\p{L}\\p{N}]*');
  return `${hasThe ? '(?:the[^\\p{L}\\p{N}]*)?' : ''}${body}(?:\\s*[!.?)$])*(?![\\p{L}\\p{N}])`;
}

const JOINER = String.raw`(?:[\s\p{P}&+]|\band\b|\by\b|\bx\b)*`;
const SUPPORT = String.raw`\s*(?:[-–—,:]\s*)?(?:w\/|w\.|with|feat\.?|ft\.?|featuring)(?![\p{L}\p{N}])`;
const FILLER = /^(?:an evening|live|in concert|live in concert|concert|show|tour|sold out)$/i;

export function resolveEventSubtitle(event) {
  if (event == null) return null;
  const title =
    typeof event.event_name === 'string' ? event.event_name.replace(/\s+/g, ' ').trim() : '';
  const headline = resolveEventDisplayName(event);
  if (!title || title === headline) return null;

  const names = [...headline.split(/\s*(?:,|&)\s*/), ...String(event.artist_names || '').split(',')]
    .map((n) => n.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(namePattern)
    .filter(Boolean);

  let rest = title
    .normalize('NFD')
    .replace(/^.{1,40}?\s+presents?\s*:?\s+/iu, '')
    .replace(/\s*\([^()]*\b(?:18|21|all ages|under|over|adults?)\b[^()]*\)/giu, '')
    .replace(/\s*[-–—]\s*sold out\s*$/iu, '');

  // The billed acts at the front: "A & B ...", "A, B, C".
  for (let changed = true; changed;) {
    changed = false;
    for (const p of names) {
      const next = rest.replace(new RegExp(`^${JOINER}${p}`, 'iu'), '');
      if (next !== rest) {
        rest = next;
        changed = true;
        break;
      }
    }
  }

  // A trailing support clause that only names billed acts: "... with Phil Wickham".
  const support = rest.match(new RegExp(`${SUPPORT}(.*)$`, 'iu'));
  if (support) {
    let body = support[1].replace(/\bspecial guests?\b/giu, '');
    for (const p of names) body = body.replace(new RegExp(p, 'giu'), '');
    if (!/[\p{L}\p{N}]/u.test(body.replace(/\b(?:and|more)\b/giu, ''))) {
      rest = rest.slice(0, support.index);
    }
  }
  rest = rest.replace(/\s*[&+]\s*more\s*!?\s*$/iu, '');

  rest = rest
    .replace(/\(\s*\)|\[\s*\]/g, '')
    .replace(/^(?:[\s\p{P}&+]|\b(?:and|y|x|w\/|with|feat|ft|featuring)\b)+/iu, '')
    .replace(/(?:[\s\-–—:|,&+/]|\b(?:and|with|feat|ft|featuring)\b)+$/iu, '') // not a final x or y: "Malcolm X"
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFC');

  if (!/[\p{L}\p{N}]/u.test(rest) || FILLER.test(rest) || fold(rest) === fold(headline))
    return null;
  return rest;
}

/** Ticketmaster's way of saying "no promoter". */
export function displayPromoterName(name) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed || /^(?:promoted by venue|promoter not defined)$/i.test(trimmed)) return null;
  return trimmed;
}
